import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import BohoNav from '@/components/BohoNav';
import BohoFooter from '@/components/BohoFooter';

interface ColorDetail {
  name: string;
  hex: string;
  description: string;
  isPantone?: boolean;
  isColoro?: boolean;
}

const colorPalette: ColorDetail[] = [
  {
    name: "Mocha Mousse",
    hex: "#997b66",
    description: "Pantone's official 2025 Color of the Year, this warm, rich brown is a versatile and comforting neutral.",
    isPantone: true
  },
  {
    name: "Transcendent Pink",
    hex: "#e0b1ae",
    description: "A soft, vintage-inspired pastel pink that adds a touch of subtle romance and femininity."
  },
  {
    name: "Future Dusk",
    hex: "#4a496b",
    description: "Coloro's 2025 Color of the Year, this deep, moody, inky blue offers a sense of stability and elegance.",
    isColoro: true
  },
  {
    name: "Icy Blue",
    hex: "#d0e5f2",
    description: "An elegant and airy pastel, this light blue is fresh, sophisticated, and a versatile accent color."
  },
  {
    name: "Cardinal Red",
    hex: "#a91d37",
    description: "A bolder, purplish-red that offers a sultry alternative to brighter reds."
  },
  {
    name: "Amber Waves",
    hex: "#e3a849",
    description: "A warm, golden yellow that evokes comfort and optimism—perfect for creating a welcoming atmosphere."
  },
  {
    name: "Sage Serenity",
    hex: "#b8c5a6",
    description: "A muted green with gray undertones that brings natural tranquility and earthy sophistication."
  },
  {
    name: "Deep Plum",
    hex: "#6b4c7a",
    description: "Rich and luxurious, this jewel-toned purple adds drama and depth to any palette."
  },
  {
    name: "Soft Lavender",
    hex: "#c8b5db",
    description: "A gentle, soothing purple that provides a calming balance to the deeper, more intense tones."
  },
  {
    name: "Cream Dream",
    hex: "#f8f5f0",
    description: "A warm, off-white neutral that serves as the perfect backdrop for showcasing bolder accent colors."
  },
  {
    name: "Dusty Rose",
    hex: "#d4a5a5",
    description: "A sophisticated blush tone that bridges warm and cool undertones for versatile styling."
  },
  {
    name: "Wispy Clouds",
    hex: "#f7f7f7",
    description: "A delicate, soft white that pairs beautifully with both the pastels and deeper jewel tones in the palette."
  }
];

const Colors2025 = () => {
  return (
    <>
      <Helmet>
        <title>2025 Color Palette - Trending Colors | Boho Style Co.</title>
        <meta name="description" content="Discover the official 2025 color trends including Pantone and Coloro's Colors of the Year. Our curated palette features sophisticated, versatile colors for modern bohemian style." />
        <meta name="keywords" content="2025 colors, Pantone color of the year, Mocha Mousse, Future Dusk, color trends, fashion colors, interior design colors" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-sage/5">
        <BohoNav />

        {/* Page Header */}
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Palette className="w-8 h-8 text-primary" />
              <h1 className="text-4xl md:text-5xl font-semibold leading-snug text-foreground">2025 Color Palette</h1>
            </div>
            <p className="text-base font-normal leading-normal text-muted-foreground">Official trending colors for the modern era</p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 pb-8">
          {/* Introduction */}
          <div className="max-w-6xl mx-auto mb-12 text-center">
            <h2 className="text-xl md:text-2xl font-semibold leading-snug text-foreground">
              The Colors Defining 2025
            </h2>
            <p className="text-lg font-normal leading-relaxed text-foreground">
              Step into the future of style with our meticulously curated palette that showcases both Pantone and Coloro's 
              official Colors of the Year. These aren't just trending hues—they're the foundation of a new aesthetic era, 
              where earthy sophistication meets contemporary elegance. Each shade tells a story of comfort, refinement, 
              and enduring beauty. As you explore our collections, let these transformative colors guide your journey 
              to discovering pieces that don't just follow trends, but define your personal style narrative. 
              Whether you're seeking that perfect statement piece or building a timeless wardrobe, these colors will 
              elevate every garment they grace.
            </p>
          </div>

          {/* Color Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {colorPalette.map((color, index) => (
              <Card key={index} className="group card-hover-standard overflow-hidden h-full flex flex-col">
                <CardHeader className="pb-4 flex-grow flex flex-col justify-between">
                  {/* Description at top */}
                  <CardDescription className="text-sm font-normal leading-normal text-muted-foreground">
                    {color.description}
                  </CardDescription>
                  
                  <div className="mt-4">
                    {/* Color Swatch */}
                    <div className="relative">
                      <div 
                        className="w-full h-32 rounded-lg shadow-inner mb-4 border border-border"
                        style={{ backgroundColor: color.hex }}
                      />
                      
                      {/* Badge overlay */}
                      <div className="absolute top-2 left-2 flex gap-2">
                        {color.isPantone && (
                        <Badge className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-xs">
                          Pantone 2025
                        </Badge>
                        )}
                      {color.isColoro && (
                        <Badge className="bg-gradient-to-r from-secondary to-secondary/80 text-secondary-foreground text-xs">
                          Coloro 2025
                        </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base md:text-lg font-medium leading-normal text-foreground">
                        {color.name}
                      </CardTitle>
                      <code className="inline-block px-3 py-1 bg-muted rounded text-sm font-mono text-muted-foreground">
                        {color.hex}
                      </code>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          {/* Palette Preview */}
          <div className="max-w-4xl mx-auto mt-16">
            <Card className="overflow-hidden">
              <CardHeader className="text-center">
                <CardTitle className="font-playfair text-2xl font-bold">Complete Palette</CardTitle>
                <CardDescription className="font-inter">
                  All colors at a glance - perfect harmony of pastels, jewel tones, and sophisticated neutrals
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-6 md:grid-cols-12 gap-2 h-20 rounded-lg overflow-hidden shadow-lg">
                  {colorPalette.map((color, index) => (
                    <div
                      key={index}
                      className="hover:scale-105 transition-transform cursor-pointer"
                      style={{ backgroundColor: color.hex }}
                      title={`${color.name} - ${color.hex}`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16 max-w-2xl mx-auto">
            <h3 className="text-xl md:text-2xl font-semibold mb-4 text-foreground">
              Ready to Embrace 2025's Color Story?
            </h3>
            <p className="text-base font-normal leading-normal text-muted-foreground mb-6">
              Discover how these trending colors come to life in our carefully curated collection. 
              Each piece is selected to showcase the beauty and versatility of this year's most coveted palette.
            </p>
            <Link to="/products">
              <Button 
                size="lg" 
                className="btn-primary px-8 py-3 rounded-full"
              >
                Shop Our Collection
              </Button>
            </Link>
          </div>
        </div>

        <BohoFooter />
      </div>
    </>
  );
};

export default Colors2025;